import React, { useState } from "react";
import {
  useCompanyTrustAccountQuery,
  useSaveCompanyTrustAccountMutation,
} from "apis/company";
import { formatDate } from "utils/helperFunctions";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <span className="text-darkgray">{label}:</span>
    <span className="text-black text-right break-all">{value || "—"}</span>
  </div>
);

const TrustRows = ({ trust }) => (
  <>
    <Row label="Sub-merchant ID" value={trust.subMerchantId} />
    <Row label="PayID domain" value={trust.payIdDomain} />
    <Row label="Trust account name" value={trust.accountName} />
    <Row
      label="BSB / account"
      value={
        trust.bsb ? `${trust.bsb} · ${trust.accountNumberMasked}` : ""
      }
    />
    <Row
      label="Bank name check"
      value={
        trust.verifiedAt
          ? `${trust.verificationResult || "Passed"} · ${formatDate(trust.verifiedAt)}`
          : ""
      }
    />
  </>
);

// Opened fresh each time, so it always starts from what is saved.
const TrustForm = ({ companyId, own, onDone }) => {
  const [values, setValues] = useState({
    subMerchantId: own?.subMerchantId || "",
    payIdDomain: own?.payIdDomain || "",
    accountName: "",
    bsb: "",
    accountNumber: "",
  });
  const [warning, setWarning] = useState(null);
  const save = useSaveCompanyTrustAccountMutation(companyId);
  const set = (key) => (e) => setValues({ ...values, [key]: e.target.value });

  const submit = () => {
    const withAccount =
      values.accountName || values.bsb || values.accountNumber;
    save.mutate(
      {
        subMerchantId: values.subMerchantId,
        payIdDomain: values.payIdDomain,
        ...(withAccount && {
          accountName: values.accountName,
          bsb: values.bsb,
          accountNumber: values.accountNumber,
        }),
      },
      {
        onSuccess: (data) =>
          data?.trustNameWarning ? setWarning(data.trustNameWarning) : onDone(),
      },
    );
  };

  const field = (key, label, placeholder) => (
    <div>
      <label className="form-label">{label}</label>
      <input
        className="form-input border border-mediumgray"
        value={values[key]}
        onChange={set(key)}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="bg-lightgray rounded-lg p-4 space-y-3">
      {field(
        "subMerchantId",
        "Sub-merchant ID",
        "32-character Submerchant ID from Monoova",
      )}
      {field("payIdDomain", "PayID domain (optional)", "example.com.au")}
      <p className="text-xs text-darkgray">
        Trust account (optional). The company owner can enter it on their
        Trust Account screen instead.
        {own?.accountNumberMasked &&
          ` Leave blank to keep ${own.bsb} · ${own.accountNumberMasked}.`}
      </p>
      {field("accountName", "Trust account name", "As held by the bank")}
      {field("bsb", "BSB", "6 digits")}
      {field("accountNumber", "Account number", "5 to 10 digits")}
      {warning && (
        <p className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded p-2">
          Saved. {warning}
        </p>
      )}
      <div className="flex gap-2 justify-end">
        <button
          className="gray-button"
          onClick={onDone}
          disabled={save.isPending}
        >
          {warning ? "Close" : "Cancel"}
        </button>
        {!warning && (
          <button
            className="black-button"
            onClick={submit}
            disabled={save.isPending}
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        )}
      </div>
    </div>
  );
};

// Where this company's buyer deposits bank: its own Monoova sub-merchant and
// trust account, or its group's.
export default function CompanyTrustAccount({ companyId }) {
  const { data, isPending, isError } = useCompanyTrustAccountQuery(companyId);
  const [editing, setEditing] = useState(false);

  return (
    <div>
      <div className="font-medium text-black mb-3 flex justify-between items-center">
        Deposit payments
        {!isPending && !isError && !editing && (
          <button
            className="text-sm text-blue-600 hover:text-blue-800"
            onClick={() => setEditing(true)}
          >
            {data?.own ? "Edit" : "Set own sub-merchant"}
          </button>
        )}
      </div>
      {isPending ? (
        <p className="text-darkgray text-sm">Loading…</p>
      ) : isError ? (
        <p className="text-red-700 text-sm">Could not load the trust account.</p>
      ) : editing ? (
        <TrustForm
          companyId={companyId}
          own={data?.own}
          onDone={() => setEditing(false)}
        />
      ) : data?.own ? (
        <div className="bg-lightgray rounded-lg p-4 space-y-3">
          <p className="text-sm text-darkgray">
            Banks apart from its group. Deposits open under this sub-merchant
            and pay out to this account.
          </p>
          <TrustRows trust={data.own} />
        </div>
      ) : (
        <div className="bg-lightgray rounded-lg p-4 space-y-3">
          <p className="text-sm text-darkgray">
            {data?.group
              ? `Uses its group's (${data.group.name}).`
              : "No group, and no sub-merchant of its own."}
          </p>
          {data?.group && <TrustRows trust={data.group} />}
        </div>
      )}
    </div>
  );
}
